import { CheckCircle2 } from 'lucide-react';
import { ASNFormData, SourceType } from './types';
import { useFormatters } from '../../../contexts/PreferencesContext';

interface ReviewStepProps {
  sourceType: SourceType;
  formData: ASNFormData;
  /** CR-6038: true when the ASN is built from supplier-imported delivery lines —
   *  weight is then user-entered rather than auto-calculated. */
  isSupplierImport?: boolean;
}

const COURIER_LABELS: Record<string, string> = {
  fedex: 'FedEx',
  ups: 'UPS',
  dhl: 'DHL',
  tnt: 'TNT',
  dpd: 'DPD',
  other: 'Other',
};

const TRANSPORT_LABELS: Record<string, string> = {
  road: 'Road',
  rail: 'Rail',
  air: 'Air',
  sea: 'Sea',
};

export function ReviewStep({ sourceType, formData, isSupplierImport = false }: ReviewStepProps) {
  const { formatDate } = useFormatters();
  const lineCount =
    sourceType === 'schedules'
      ? formData.schedules.length
      : formData.delivery_line_ids.length;
  const sourceLabel =
    sourceType === 'schedules' ? 'PO Line Schedules' : 'Call-Off Delivery Lines';

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-900">
        <div className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            <strong>ASN number</strong> will be auto-generated on save (e.g. ASN000001).{' '}
            {isSupplierImport ? (
              <>
                <strong>Distance</strong> is auto-calculated on save (from your dispatch site to
                the customer site); <strong>Weight</strong> is the value you entered.
              </>
            ) : (
              <>
                <strong>Weight</strong> and <strong>Distance</strong> are auto-calculated on save
                (weight from the parts master, distance from your dispatch site to the customer
                site).
              </>
            )}{' '}
            Review the details below and click <strong>Create ASN</strong> to submit.
          </span>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Row label="Source" value={sourceLabel} />
        <Row label="Lines selected" value={String(lineCount)} />
        <Row
          label="Courier"
          value={
            formData.courier === 'other'
              ? formData.courier_other || 'Other'
              : COURIER_LABELS[formData.courier] ?? formData.courier
          }
        />
        <Row label="Tracking #" value={formData.tracking_number || '—'} />
        <Row label="Invoice #" value={formData.invoice_number || '—'} />
        <Row
          label="Transport"
          value={TRANSPORT_LABELS[formData.method_of_transport] ?? formData.method_of_transport}
        />
        <Row
          label={sourceType === 'delivery_lines' ? 'Pickup date' : 'Expected delivery'}
          value={
            formData.expected_delivery_date
              ? formatDate(formData.expected_delivery_date)
              : '—'
          }
        />
        <Row
          label="Weight (kg)"
          value={isSupplierImport ? (formData.weight_kg || '—') : 'Auto-calculated'}
        />
        <Row label="Distance (km)" value="Auto-calculated" />
      </dl>

      {formData.notes && (
        <div className="text-sm">
          <div className="font-medium text-muted-foreground mb-1">Notes</div>
          <div className="rounded-md border bg-muted/30 px-3 py-2 whitespace-pre-wrap">
            {formData.notes}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </>
  );
}
